/**
 * `MobileDrawer` — ported from `Foyer_Dashboard_CURRENT.jsx` line 717.
 * Mirrors `TopBar`'s tabs + account menu in a slide-in drawer. Phase-2
 * tabs receive the same `aria-disabled` + tooltip treatment. The SITES
 * list was removed 2026-07-06 (founder): the standalone top switcher
 * already owns site switching on mobile — the drawer duplicated it.
 * Sign out added (mobile previously had no logout).
 */

import { motion } from 'framer-motion';
import { Briefcase, Building2, CreditCard, LogOut, Moon, Settings as SettingsIcon, Sun, X } from 'lucide-react';

import { useAgency } from '../../app/agency-context.js';
import { useAuth } from '../../app/auth-context.js';
import { usePendingEscalationsStore } from '../../app/pending-escalations-context.js';
import { useSites } from '../../app/providers.js';
import type { View } from '../../app/view-router.js';
import { PHASE_2_VIEWS } from '../../app/view-router.js';
import { useC, useTheme } from '../../lib/theme.js';
import { AccountSwitcher } from './AccountSwitcher.js';
import { useSiteTabs } from './site-tabs.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

export interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  view: View;
  setView: (v: View) => void;
}

export function MobileDrawer({ open, onClose, view, setView }: MobileDrawerProps): JSX.Element | null {
  const C = useC();
  const { theme, setTheme } = useTheme();
  const auth = useAuth();
  const { activeSite, setActiveSite } = useSites();
  const { features, acting } = useAgency();
  // Pending-escalation count badge on the Resolution tab — mirrors the
  // TopBar badge so the count is visible on mobile too. Reads the shared
  // store (not its own poller) so mobile + desktop + the Resolution queue
  // all show the same live count. Hook runs unconditionally (before the
  // early return) per the rules of hooks.
  const { count: pendingEscalations } = usePendingEscalationsStore();
  const siteTabs = useSiteTabs(view);

  if (!open) return null;

  const navTo = (v: View): void => {
    setView(v);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] md:hidden"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm flex flex-col"
        style={{ background: C.bg, borderLeft: `1px solid ${C.border}` }}
      >
        <div
          className="flex items-center justify-between p-4"
          style={{ borderBottom: `1px solid ${C.border}` }}
        >
          <span style={{ fontFamily: serifFont, fontSize: 22, color: C.text, letterSpacing: -0.5 }}>
            Menu
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="w-8 h-8 flex items-center justify-center"
            style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
          >
            <X size={14} color={C.muted} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {/* Agency account switcher — the top bar hides it below `sm`
              (no room next to the site picker), so phones get it here.
              First item in the drawer (founder 2026-10-11). Renders nothing unless the user is agency-enabled or acting. */}
          <div className="sm:hidden mb-4">
            <AccountSwitcher inline />
          </div>

          {activeSite ? (
            <>
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 2,
                  marginBottom: 8,
                }}
              >
                {activeSite.domain.toUpperCase()}
              </div>
              <div className="space-y-0.5 mb-6">
                {siteTabs.map((t) => {
                  const disabled = PHASE_2_VIEWS.has(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={disabled ? undefined : () => navTo(t.id)}
                      aria-disabled={disabled || undefined}
                      className="w-full text-left px-3 py-2.5 flex items-center justify-between"
                      style={{
                        fontFamily: sansFont,
                        fontSize: 14,
                        color: view === t.id ? C.text : C.muted,
                        background: view === t.id ? C.surface2 : 'transparent',
                        borderRadius: 2,
                        opacity: disabled ? 0.45 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <span>{t.label}</span>
                      {/* Touch has no hover: the reason a tab is disabled is
                          visible text, not a title tooltip (ux.md §12.3). */}
                      {disabled ? (
                        <span style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1, color: C.muted }}>
                          PHASE 2
                        </span>
                      ) : null}
                      {t.id === 'resolution' && pendingEscalations > 0 ? (
                        <span
                          aria-label={`${pendingEscalations} pending escalations`}
                          style={{
                            padding: '1px 6px',
                            background: C.lime,
                            color: C.ink,
                            borderRadius: 2,
                            fontFamily: monoFont,
                            fontSize: 10,
                            fontWeight: 600,
                          }}
                        >
                          {pendingEscalations > 99 ? '99+' : pendingEscalations}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 8,
            }}
          >
            ACCOUNT
          </div>
          <div className="space-y-0.5">
            <button
              type="button"
              onClick={() => {
                setActiveSite(null);
                navTo('sites');
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
              style={{ fontFamily: sansFont, fontSize: 14, color: C.text }}
            >
              <Building2 size={13} color={C.muted} /> All sites
            </button>
            {features?.agency === true && acting === null ? (
              <button
                type="button"
                data-testid="mobile-drawer-agency-portal"
                onClick={() => {
                  setActiveSite(null);
                  navTo('agencyPortal');
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                style={{ fontFamily: sansFont, fontSize: 14, color: C.text }}
              >
                <Briefcase size={13} color={C.muted} /> Agency Portal
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setActiveSite(null);
                navTo('billing');
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
              style={{ fontFamily: sansFont, fontSize: 14, color: C.text }}
            >
              <CreditCard size={13} color={C.muted} /> Billing
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveSite(null);
                navTo('account');
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
              style={{ fontFamily: sansFont, fontSize: 14, color: C.text }}
            >
              <SettingsIcon size={13} color={C.muted} /> Account
            </button>
            <button
              type="button"
              onClick={() => {
                onClose();
                void auth.signOut();
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
              style={{ fontFamily: sansFont, fontSize: 14, color: C.muted }}
            >
              <LogOut size={13} color={C.muted} /> Sign out
            </button>
          </div>
        </div>
        <div
          className="p-4 flex items-center justify-between"
          style={{ borderTop: `1px solid ${C.border}` }}
        >
          <span style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Theme</span>
          <button
            type="button"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label="Toggle theme"
            className="flex items-center gap-2 px-3 py-1.5"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            {theme === 'dark' ? <Sun size={13} color={C.muted} /> : <Moon size={13} color={C.muted} />}
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>
              {theme === 'dark' ? 'Light' : 'Dark'}
            </span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
